import qrcode from "qrcode-generator";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import {
  isDanhyMobileDevice,
  validationRegex,
} from "./utils";
import { openDanhyAppDeeplink as launchDanhyAppOrStore } from "./_DanhyDeeplink";

const QR_MSG = {
  booking_danhy_qr_loading: { en: "Loading QR code...", vi: "Đang tải mã QR..." },
  booking_danhy_qr_scanning: { en: "Scanning QR code...", vi: "Đang quét mã QR..." },
  booking_danhy_qr_failed: {
    en: "Could not load QR code. Please try again.",
    vi: "Không thể tải mã QR. Vui lòng thử lại.",
  },
  booking_danhy_qr_expiry: { en: "The code will expire in: <span class='blue-txt bold'>{time}s</span>", vi: "Mã sẽ hết hạn sau: <span class='blue-txt bold'>{time}s</span>" },
  booking_danhy_qr_expired: { en: "The code has expired", vi: "Mã đã hết hạn" },
  booking_danhy_success_title: { en: "Login verification successful", vi: "Xác minh đăng nhập thành công" },
  booking_danhy_success_desc: {
    en: "Your login has been successfully verified. Hoan My uses your information from the Danh Y App to fill in the appointment registration details.",
    vi: "Đăng nhập của bạn đã được xác minh thành công. Hoàn Mỹ sử dụng thông tin từ ứng dụng Danh Y để điền vào đăng ký lịch hẹn.",
  },
  booking_danhy_error_title: { en: "Login verification failed", vi: "Xác minh đăng nhập thất bại" },
  booking_danhy_error_desc: {
    en: "Login verification error, please check again or try the operation again.",
    vi: "Lỗi xác minh đăng nhập, vui lòng kiểm tra lại hoặc thử lại.",
  },
  booking_danhy_profile_error_title: {
    en: "Unable to load profile",
    vi: "Không thể tải thông tin người dùng",
  },
  booking_danhy_profile_error_desc: {
    en: "We could not retrieve your information from the Danh Y App. Please try again.",
    vi: "Không thể lấy thông tin từ ứng dụng Danh Y. Vui lòng thử lại.",
  },
  booking_danhy_rejected_title: { en: "Login verification reject", vi: "Từ chối xác minh đăng nhập" },
  booking_danhy_rejected_desc: {
    en: "Login verification rejected, please check again or try the operation again",
    vi: "Xác minh đăng nhập bị từ chối, vui lòng kiểm tra lại hoặc thử lại.",
  },
  booking_danhy_register_identifier_invalid: {
    en: "Phone/Email is not in the correct format.",
    vi: "Số điện thoại/Email không đúng định dạng.",
  },
  booking_danhy_register_success_title: {
    en: "Registration successful",
    vi: "Đăng ký thành công",
  },
  booking_danhy_register_success_desc: {
    en: "Your Danh Y account has been created. Scan the QR code to log in and continue booking.",
    vi: "Tài khoản Danh Y của bạn đã được tạo. Quét mã QR để đăng nhập và tiếp tục đặt lịch.",
  },
  booking_danhy_register_failed: {
    en: "Registration failed. Please check your details and try again.",
    vi: "Đăng ký thất bại. Vui lòng kiểm tra thông tin và thử lại.",
  },
};

function t(key, replacements = {}) {
  const lang =
    typeof HM_Variables !== "undefined" && HM_Variables.language === "en"
      ? "en"
      : "vi";
  let text = HM_Variables?.frontend_lang?.[key] || QR_MSG[key]?.[lang] || key;
  Object.entries(replacements).forEach(([placeholder, value]) => {
    text = String(text).split(`{${placeholder}}`).join(String(value));
  });
  return text;
}

function danhyApi(action, signal, payload) {
  const root = HM_Variables?.root_url || "";
  const nonce = HM_Variables?.rest_nonce || "";
  const body = { action };
  if (payload !== undefined) {
    body.payload = payload;
  }

  return fetch(`${root}/wp-json/hmc/v1/danhy`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-WP-Nonce": nonce,
    },
    body: JSON.stringify(body),
    signal,
    credentials: "same-origin",
  }).then((r) => r.json());
}

const DANHY_LOCKED_CLASS = "is-danhy-locked";

function lockDanhyTextInput($form, name) {
  $form
    .find(`input[name="${name}"]`)
    .prop("readonly", true)
    .attr("aria-readonly", "true")
    .addClass(DANHY_LOCKED_CLASS);
}

function lockDanhyCustomSelect($form, listSelector) {
  const $select = $form.find(listSelector).closest(".custom-select");
  if (!$select.length) {
    return;
  }

  if ($select.attr("data-danhy-tabindex") == null) {
    $select.attr("data-danhy-tabindex", $select.attr("tabindex") || "");
  }

  $select
    .addClass(DANHY_LOCKED_CLASS)
    .attr({
      "aria-disabled": "true",
      tabindex: "-1",
    });
}

function lockDanhyGender($form) {
  $form
    .find('input[name="gender"]')
    .closest(".radio-group")
    .addClass(DANHY_LOCKED_CLASS)
    .attr("aria-disabled", "true");
}

function unlockDanhyLockedFields($form) {
  const $target =
    $form && $form.length ? $form : $("#integrated-booking-form");
  if (!$target.length) {
    return;
  }

  $target.removeAttr("data-danhy-logged-in");

  $target
    .find(`input.${DANHY_LOCKED_CLASS}`)
    .prop("readonly", false)
    .removeAttr("aria-readonly")
    .removeClass(DANHY_LOCKED_CLASS);

  $target.find(`.custom-select.${DANHY_LOCKED_CLASS}`).each((_, el) => {
    const $select = $(el);
    const prevTab = $select.attr("data-danhy-tabindex");
    $select
      .removeClass(DANHY_LOCKED_CLASS)
      .removeAttr("aria-disabled")
      .removeAttr("data-danhy-tabindex");
    if (prevTab) {
      $select.attr("tabindex", prevTab);
    } else {
      $select.removeAttr("tabindex");
    }
  });

  $target
    .find(`.radio-group.${DANHY_LOCKED_CLASS}`)
    .removeClass(DANHY_LOCKED_CLASS)
    .removeAttr("aria-disabled");
}

function encodeQr(content) {
  const qr = qrcode(0, "M");
  qr.addData(String(content));
  qr.make();
  return qr.createDataURL(4, 0);
}

export default class QRLogin {
  constructor() {
    this.$modal = $("#booking-danhy-modal");
    this.$registerModal = $("#booking-danhy-register-modal");
    this.$registerForm = $("#booking-danhy-register-form");
    this.$qrMain = this.$modal.find(".booking-danhy-qr-main");
    this.$qrExpiry = this.$modal.find(".booking-danhy-qr-expiry");
    this.$title = this.$modal.find(".modal-title");
    this.$desc = this.$modal.find(".modal-desc");
    this.$result = this.$modal.find(".booking-danhy-result");
    this.$resultIcon = this.$modal.find(".booking-danhy-result-icon");
    this.$submit = this.$modal.find(".booking-danhy-submit");
    this.$continue = this.$modal.find(".booking-danhy-continue");
    this.$retry = this.$modal.find(".booking-danhy-retry");
    this.$howtoModal = $("#booking-danhy-howto-modal");
    this.$howtoPrev = this.$howtoModal.find(".booking-danhy-howto-prev");
    this.$howtoNext = this.$howtoModal.find(".booking-danhy-howto-next");
    this.howtoStep = 0;
    this.howtoStepCount = 3;
    this.defaultTitle = this.$title.html();
    this.defaultDesc = this.$desc.html();
    this._abortController = null;
    this._expiryTimer = null;
    this._accessTokenTimer = null;
    this.sessionId = null;
    this.accessToken = null;
    this._exchanging = false;
    this._stomp = null;
    this._socketReady = null;
    this._socketReadyResolve = null;
    this._socketReadyReject = null;
    this._registerSubmitInFlight = false;
    this._pendingRegisterLogin = false;
    this._subscription = null;

    $(document)
      .off("click.bookingDanhyOpen")
      .on("click.bookingDanhyOpen", ".booking-danhy-cta", (event) => {
        event.preventDefault();
        if (isDanhyMobileDevice()) {
          this.openDanhyAppDeeplink();
          return;
        }
        this.openModal();
      });

    $(document)
      .off("click.bookingDanhyClose")
      .on("click.bookingDanhyClose", ".booking-danhy-close", (event) => {
        event.preventDefault();
        this.closeModal();
      });

    $(document)
      .off("click.bookingDanhyHowToOpen")
      .on("click.bookingDanhyHowToOpen", ".booking-danhy-how-to", (event) => {
        event.preventDefault();
        this.openHowToModal();
      });

    $(document)
      .off("click.bookingDanhyHowToClose")
      .on("click.bookingDanhyHowToClose", ".booking-danhy-howto-close", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeHowToModal();
      });

    $(document)
      .off("click.bookingDanhyHowToPrev")
      .on("click.bookingDanhyHowToPrev", ".booking-danhy-howto-prev", (event) => {
        event.preventDefault();
        this.setHowToStep(this.howtoStep - 1);
      });

    $(document)
      .off("click.bookingDanhyHowToNext")
      .on("click.bookingDanhyHowToNext", ".booking-danhy-howto-next", (event) => {
        event.preventDefault();
        this.setHowToStep(this.howtoStep + 1);
      });

    $(document)
      .off("click.bookingDanhyHowToDot")
      .on("click.bookingDanhyHowToDot", ".booking-danhy-howto-dot", (event) => {
        event.preventDefault();
        const step = Number($(event.currentTarget).data("howto-dot"));
        if (Number.isFinite(step)) {
          this.setHowToStep(step);
        }
      });

    $(document)
      .off("click.bookingDanhyRegisterOpen")
      .on("click.bookingDanhyRegisterOpen", ".booking-danhy-submit", (event) => {
        event.preventDefault();
        this.openRegisterModal();
      });

    $(document)
      .off("click.bookingDanhyRegisterClose")
      .on(
        "click.bookingDanhyRegisterClose",
        ".booking-danhy-register-close",
        (event) => {
          event.preventDefault();
          this.closeRegisterModal();
        },
      );

    $(document)
      .off("keydown.bookingDanhyModal")
      .on("keydown.bookingDanhyModal", (event) => {
        if (event.key !== "Escape") {
          return;
        }
        if (this.$howtoModal.hasClass("show-modal")) {
          this.closeHowToModal();
          return;
        }
        if (this.$registerModal.hasClass("show-modal")) {
          this.closeRegisterModal();
          return;
        }
        if (this.$modal.hasClass("show-modal")) {
          this.closeModal();
        }
      });

    $(document)
      .off("click.bookingDanhyRetry")
      .on("click.bookingDanhyRetry", ".booking-danhy-retry", (event) => {
        event.preventDefault();
        if (isDanhyMobileDevice()) {
          this.openDanhyAppDeeplink();
          return;
        }
        this.initQr();
      });

    $(document)
      .off("click.bookingDanhyContinue")
      .on("click.bookingDanhyContinue", ".booking-danhy-continue", (event) => {
        event.preventDefault();
        this.onContinue();
      });

    this.$registerForm
      .off("submit.bookingDanhyRegister")
      .on("submit.bookingDanhyRegister", (event) => {
        event.preventDefault();
        this.onRegisterSubmit();
      });

    this.$registerForm
      .off("click.bookingDanhyPasswordToggle")
      .on(
        "click.bookingDanhyPasswordToggle",
        ".hm-password-toggle",
        (event) => {
          event.preventDefault();
          this.togglePasswordVisibility($(event.currentTarget));
        },
      );

    this.$registerForm
      .off("input.bookingDanhyPasswordPolicy")
      .on(
        "input.bookingDanhyPasswordPolicy",
        'input[name="danhy_register_password"]',
        (event) => {
          this.updatePasswordPolicies(String($(event.currentTarget).val() || ""));
          this.validateRegisterConfirmMatch({ silentIfEmpty: true });
          this.syncRegisterSubmitState();
        },
      );

    this.$registerForm
      .off("input.bookingDanhyPasswordConfirm")
      .on(
        "input.bookingDanhyPasswordConfirm",
        'input[name="danhy_register_password_confirm"]',
        () => {
          this.validateRegisterConfirmMatch({ silentIfEmpty: true });
          this.syncRegisterSubmitState();
        },
      );

    this.$registerForm
      .off("input.bookingDanhyRegisterIdentifier")
      .on(
        "input.bookingDanhyRegisterIdentifier",
        'input[name="danhy_register_identifier"]',
        () => {
          this.validateRegisterIdentifier({ silentIfEmpty: true });
          this.syncRegisterSubmitState();
        },
      );
  }

  setContinueLoading(isLoading) {
    this.$continue
      .prop("disabled", !!isLoading)
      .toggleClass("disabled", !!isLoading);
  }

  async onContinue() {
    if (this.$continue.prop("disabled")) {
      return;
    }

    if (this._pendingRegisterLogin) {
      this._pendingRegisterLogin = false;
      this.restoreDefaultLayout();
      this.connectSocket();
      this.initQr();
      return;
    }

    this.setContinueLoading(true);

    const payload = {};
    if (this.accessToken) {
      payload.access_token = this.accessToken;
    }

    try {
      const res = await this.fetchUserInfo(
        Object.keys(payload).length ? payload : undefined,
      );

      if (!res?.success) {
        this.showResult("profile_error");
        return;
      }

      this.fillBookingForm(res.data || {});
      this.closeModal();
    } catch (error) {
      console.error("user_info failed");
      this.showResult("profile_error");
    } finally {
      this.setContinueLoading(false);
    }
  }

  async fetchUserInfo(payload) {
    return danhyApi(
      "user_info",
      undefined,
      payload && Object.keys(payload).length ? payload : undefined,
    );
  }

  async restoreSessionIfNeeded() {
    const $form = $("#integrated-booking-form");
    if (!$form.length || $form.attr("data-danhy-logged-in") !== "1") {
      return;
    }

    this.hideDanhyLogin();

    try {
      const res = await this.fetchUserInfo();
      if (!res?.success) {
        this.handleSessionRestoreFailure();
        return;
      }

      this.fillBookingForm(res.data || {});
      this.hideDanhyLogin();
    } catch (error) {
      console.error("user_info restore failed");
      this.handleSessionRestoreFailure();
    }
  }

  handleSessionRestoreFailure() {
    unlockDanhyLockedFields($("#integrated-booking-form"));
    this.showDanhyLogin();
  }

  mapGender(maGioiTinh) {
    const raw = String(maGioiTinh || "")
      .trim()
      .toUpperCase();
    if (!raw) {
      return null;
    }

    if (["1", "M", "NAM", "MALE"].includes(raw)) {
      return "male";
    }
    if (["2", "F", "NU", "NỮ", "FEMALE"].includes(raw)) {
      return "female";
    }
    return null;
  }

  formatBirthday(ngaySinh) {
    const ms = Number(ngaySinh);
    if (!Number.isFinite(ms) || ms <= 0) {
      return "";
    }
    const date = new Date(ms);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  selectCustomOption($form, listSelector, value) {
    const normalized = value == null ? "" : String(value).trim();
    if (!normalized) {
      return false;
    }

    const $list = $form.find(listSelector);
    const $option = $list
      .find(".option")
      .filter((_, el) => String($(el).data("value")) === normalized)
      .first();
    if (!$option.length) {
      return false;
    }

    const $select = $list.closest(".custom-select");
    const $hidden = $select.find("input[type='hidden']");
    $list.find(".option").removeClass("active");
    $option.addClass("active");
    $select
      .find(".current-value")
      .html($option.html())
      .removeClass("default-value");
    $hidden.val(normalized).trigger("change");
    return true;
  }

  lockDanhyProfileFields(
    $form,
    {
      name = false,
      cccd = false,
      email = false,
      phone = false,
      address = false,
      birthday = false,
      province = false,
      ward = false,
      gender = false,
    } = {},
  ) {
    const $target =
      $form && $form.length ? $form : $("#integrated-booking-form");
    if (!$target.length) {
      return;
    }

    $target.attr("data-danhy-logged-in", "1");

    if (name) {
      lockDanhyTextInput($target, "name");
    }
    if (cccd) {
      lockDanhyTextInput($target, "cccd");
    }
    if (email) {
      lockDanhyTextInput($target, "email");
    }
    if (phone) {
      lockDanhyTextInput($target, "phone");
    }
    if (address) {
      lockDanhyTextInput($target, "dia_chi_khach_hang");
    }
    if (birthday) {
      lockDanhyTextInput($target, "birthday");
    }
    if (province) {
      lockDanhyCustomSelect($target, "#province-select-list");
    }
    if (ward) {
      lockDanhyCustomSelect($target, "#ward-select-list");
    }
    if (gender) {
      lockDanhyGender($target);
    }
  }

  fillBookingForm(profile) {
    const $form = $("#integrated-booking-form");
    if (!$form.length || !profile || typeof profile !== "object") {
      return;
    }

    const setText = (name, value) => {
      if (value == null || String(value).trim() === "") {
        return false;
      }
      $form.find(`input[name="${name}"]`).val(String(value).trim()).trigger("input");
      return true;
    };

    const hasName = setText("name", profile.hoTen);
    const hasCccd = setText("cccd", profile.cmnd);
    const hasEmail = setText("email", profile.email);
    const hasPhone = setText("phone", profile.soDienThoai);
    const hasAddress = setText("dia_chi_khach_hang", profile.diaChi);

    const birthday = this.formatBirthday(profile.ngaySinh);
    const hasBirthday = !!birthday;
    if (hasBirthday) {
      $form.find('input[name="birthday"]').val(birthday).trigger("change");
    }

    const gender = this.mapGender(profile.maGioiTinh);
    const hasGender = !!gender;
    if (hasGender) {
      $form
        .find(`input[name="gender"][value="${gender}"]`)
        .prop("checked", true)
        .trigger("change");
    }

    const hasBhyt = !!profile.maBaoHiemYTe;
    $form
      .find(`input[name="cothebhyt"][value="${hasBhyt ? "true" : "false"}"]`)
      .prop("checked", true)
      .trigger("change");

    const hasProvince = profile.cityId
      ? this.selectCustomOption($form, "#province-select-list", profile.cityId)
      : false;
    const hasWard = profile.wardId
      ? this.selectCustomOption($form, "#ward-select-list", profile.wardId)
      : false;

    this.lockDanhyProfileFields($form, {
      name: hasName,
      cccd: hasCccd,
      email: hasEmail,
      phone: hasPhone,
      address: hasAddress,
      birthday: hasBirthday,
      province: hasProvince,
      ward: hasWard,
      gender: hasGender,
    });
  }

  hideDanhyLogin() {
    $(".booking-form-crm-danhy").prop("hidden", true);
  }

  showDanhyLogin() {
    $(".booking-form-crm-danhy").prop("hidden", false);
  }

  clearAccessToken() {
    clearTimeout(this._accessTokenTimer);
    this._accessTokenTimer = null;
    this.accessToken = null;
  }

  storeMemoryAccessToken(token, expiresIn) {
    this.clearAccessToken();
    this.accessToken = token || null;
    if (!this.accessToken) {
      return;
    }

    const ttlMs = Math.max(60, Number(expiresIn) || 300) * 1000;
    this._accessTokenTimer = setTimeout(() => {
      this.accessToken = null;
      this._accessTokenTimer = null;
    }, ttlMs);
  }

  closeModal() {
    if (!this.$modal.length) {
      return;
    }

    this.closeHowToModal();
    this.$modal.removeClass("show-modal").attr("aria-hidden", "true");
    this.$modal.find(".modal-dialog").removeClass("active");

    if (
      $(".modal-wrapper.show-modal")
        .not(this.$modal)
        .not(this.$registerModal)
        .not(this.$howtoModal).length === 0
    ) {
      $("body").removeClass("show-modal");
    }

    // Keep SockJS warm for the next open; only drop the QR topic subscription.
    this.unsubscribeSession();
    // Keep current UI while the modal finishes hiding — restoring the QR
    // layout here caused a brief flash of the QR screen before close.
    this.reset({ restoreLayout: false });
  }

  isQrModalOpen() {
    return !!this.$modal?.length && this.$modal.hasClass("show-modal");
  }

  /**
   * Start SockJS/STOMP early (e.g. on booking page load) so openModal is faster.
   */
  warmSocket() {
    this.connectSocket().catch(() => {
      // Ignore warm-up failures; initQr will retry/surface errors.
    });
  }

  openHowToModal() {
    if (!this.$howtoModal.length) {
      return;
    }

    this.setHowToStep(0);
    $("body").addClass("show-modal");
    this.$howtoModal.addClass("show-modal").attr("aria-hidden", "false");
    this.$howtoModal.find(".modal-dialog").addClass("active");

    const $close = this.$howtoModal.find(".close-modal-btn.booking-danhy-howto-close");
    if ($close.length) {
      setTimeout(() => $close.trigger("focus"), 50);
    }
  }

  closeHowToModal() {
    if (!this.$howtoModal.length || !this.$howtoModal.hasClass("show-modal")) {
      return;
    }

    this.$howtoModal.removeClass("show-modal").attr("aria-hidden", "true");
    this.$howtoModal.find(".modal-dialog").removeClass("active");

    if ($(".modal-wrapper.show-modal").not(this.$howtoModal).length === 0) {
      $("body").removeClass("show-modal");
    }

    const $trigger = this.$modal.find(".booking-danhy-how-to").first();
    if ($trigger.length && this.$modal.hasClass("show-modal")) {
      $trigger.trigger("focus");
    }
  }

  setHowToStep(step) {
    if (!this.$howtoModal.length) {
      return;
    }

    const next = Math.max(0, Math.min(this.howtoStepCount - 1, Number(step) || 0));
    this.howtoStep = next;
    this.$howtoModal.attr("data-step", next);

    this.$howtoModal.find(".booking-danhy-howto-slide").each((index, el) => {
      const active = index === next;
      $(el).toggleClass("is-active", active).prop("hidden", !active);
    });

    this.$howtoModal.find(".booking-danhy-howto-dot").each((index, el) => {
      const $dot = $(el);
      $dot
        .toggleClass("is-current", index === next)
        .toggleClass("is-done", index < next);
      if (index === next) {
        $dot.attr("aria-current", "step");
      } else {
        $dot.removeAttr("aria-current");
      }
    });

    this.$howtoPrev.prop("disabled", next === 0);
    this.$howtoNext.prop("disabled", next === this.howtoStepCount - 1);
  }

  openRegisterModal() {
    if (!this.$registerModal.length) {
      return;
    }

    // Abort in-flight QR init; showResult() ignores late errors once QR modal is closed.
    this.closeModal();
    this.resetRegisterPasswordFields();
    this.clearRegisterFormErrors();
    this.$registerForm?.[0]?.reset();
    this.syncRegisterSubmitState();

    $("body").addClass("show-modal");
    this.$registerModal.addClass("show-modal").attr("aria-hidden", "false");
    this.$registerModal.find(".modal-dialog").addClass("active");

    const $first = this.$registerForm.find(
      'input[name="danhy_register_identifier"]',
    );
    if ($first.length) {
      setTimeout(() => $first.trigger("focus"), 50);
    }
  }

  closeRegisterModal() {
    if (!this.$registerModal.length) {
      return;
    }

    this.$registerModal.removeClass("show-modal").attr("aria-hidden", "true");
    this.$registerModal.find(".modal-dialog").removeClass("active");
    this.clearRegisterFormErrors();
    this.resetRegisterPasswordFields();

    if (
      $(".modal-wrapper.show-modal")
        .not(this.$registerModal)
        .not(this.$modal).length === 0
    ) {
      $("body").removeClass("show-modal");
    }
  }

  clearRegisterFormErrors() {
    if (!this.$registerForm?.length) {
      return;
    }
    this.$registerForm.find(".hm-input").removeClass("err");
    this.$registerForm.find(".err-msg").text("");
  }

  togglePasswordVisibility($toggle) {
    if (!$toggle?.length) {
      return;
    }

    const $field = $toggle.closest(".hm-password-field");
    const $input = $field.find("input.hm-input").first();
    if (!$input.length) {
      return;
    }

    const showing = $input.attr("type") === "text";
    const nextType = showing ? "password" : "text";
    $input.attr("type", nextType);

    const $showIcon = $toggle.find(".hm-password-toggle__icon--show");
    const $hideIcon = $toggle.find(".hm-password-toggle__icon--hide");
    if (nextType === "text") {
      $showIcon.prop("hidden", true);
      $hideIcon.prop("hidden", false);
      $toggle.attr(
        "aria-label",
        $toggle.data("label-hide") || $toggle.attr("aria-label"),
      );
    } else {
      $showIcon.prop("hidden", false);
      $hideIcon.prop("hidden", true);
      $toggle.attr(
        "aria-label",
        $toggle.data("label-show") || $toggle.attr("aria-label"),
      );
    }
  }

  resetRegisterPasswordFields() {
    if (!this.$registerForm?.length) {
      return;
    }

    this.$registerForm.find(".hm-password-field").each((_, field) => {
      const $field = $(field);
      const $input = $field.find("input.hm-input").first();
      const $toggle = $field.find(".hm-password-toggle").first();
      $input.attr("type", "password");
      $toggle.find(".hm-password-toggle__icon--show").prop("hidden", false);
      $toggle.find(".hm-password-toggle__icon--hide").prop("hidden", true);
      $toggle.attr(
        "aria-label",
        $toggle.data("label-show") || $toggle.attr("aria-label"),
      );
    });

    this.updatePasswordPolicies("");
  }

  getPasswordPolicyState(password) {
    const value = String(password || "");
    return {
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      special: /[^A-Za-z0-9]/.test(value),
    };
  }

  isPasswordPolicyMet(password) {
    const state = this.getPasswordPolicyState(password);
    return Object.values(state).every(Boolean);
  }

  updatePasswordPolicies(password) {
    if (!this.$registerForm?.length) {
      return;
    }

    const state = this.getPasswordPolicyState(password);
    this.$registerForm.find(".password-policies__item").each((_, item) => {
      const $item = $(item);
      const key = String($item.data("policy") || "");
      $item.toggleClass("is-met", !!state[key]);
    });
  }

  isValidRegisterIdentifier(value) {
    const identifier = String(value || "").trim();
    if (!identifier) {
      return false;
    }

    if (validationRegex.emailRegex.test(identifier)) {
      return true;
    }

    let digits = identifier.replace(/\D/g, "");
    if (digits.startsWith("84") && digits.length >= 11) {
      digits = `0${digits.slice(2)}`;
    }

    return validationRegex.phoneRegex.test(digits);
  }

  validateRegisterIdentifier({ silentIfEmpty = false } = {}) {
    if (!this.$registerForm?.length) {
      return true;
    }

    const identifier = String(
      this.$registerForm.find('input[name="danhy_register_identifier"]').val() ||
        "",
    ).trim();
    const $input = this.$registerForm.find(
      'input[name="danhy_register_identifier"]',
    );
    const $err = $input.closest(".input-group").find(".err-msg");

    if (!identifier) {
      if (silentIfEmpty) {
        $input.removeClass("err");
        $err.text("");
        return true;
      }
      return false;
    }

    if (!this.isValidRegisterIdentifier(identifier)) {
      $input.addClass("err");
      $err.text(
        t("booking_danhy_register_identifier_invalid") ||
          "Email/Phone is not in the correct format.",
      );
      return false;
    }

    $input.removeClass("err");
    $err.text("");
    return true;
  }

  validateRegisterConfirmMatch({ silentIfEmpty = false } = {}) {
    if (!this.$registerForm?.length) {
      return true;
    }

    const password = String(
      this.$registerForm.find('input[name="danhy_register_password"]').val() ||
        "",
    );
    const confirm = String(
      this.$registerForm
        .find('input[name="danhy_register_password_confirm"]')
        .val() || "",
    );
    const $confirmInput = this.$registerForm.find(
      'input[name="danhy_register_password_confirm"]',
    );
    const $err = $confirmInput.closest(".input-group").find(".err-msg");

    if (!confirm) {
      if (silentIfEmpty) {
        $confirmInput.removeClass("err");
        $err.text("");
        return true;
      }
      return false;
    }

    const lang =
      typeof HM_Variables !== "undefined" && HM_Variables.language === "en"
        ? "en"
        : "vi";
    const mismatchMsg =
      lang === "en"
        ? "Passwords do not match."
        : "Mật khẩu xác nhận không khớp.";

    if (password !== confirm) {
      $confirmInput.addClass("err");
      $err.text(mismatchMsg);
      return false;
    }

    $confirmInput.removeClass("err");
    $err.text("");
    return true;
  }

  isRegisterFormReady() {
    if (!this.$registerForm?.length) {
      return false;
    }

    const identifier = String(
      this.$registerForm.find('input[name="danhy_register_identifier"]').val() ||
        "",
    ).trim();
    const password = String(
      this.$registerForm.find('input[name="danhy_register_password"]').val() ||
        "",
    );
    const confirm = String(
      this.$registerForm
        .find('input[name="danhy_register_password_confirm"]')
        .val() || "",
    );

    return (
      this.isValidRegisterIdentifier(identifier) &&
      this.isPasswordPolicyMet(password) &&
      confirm !== "" &&
      password === confirm
    );
  }

  syncRegisterSubmitState() {
    if (!this.$registerForm?.length) {
      return;
    }

    const $btn = this.$registerForm.find(".booking-danhy-register-submit");
    const ready = this.isRegisterFormReady();
    $btn.prop("disabled", !ready).toggleClass("disabled", !ready);
  }

  setRegisterSubmitLoading(isLoading) {
    if (!this.$registerForm?.length) {
      return;
    }
    const $btn = this.$registerForm.find(".booking-danhy-register-submit");
    $btn.prop("disabled", !!isLoading).toggleClass("disabled", !!isLoading);
    $btn.toggleClass("is-loading", !!isLoading);
  }

  getRegisterSendErrorMessage(res = {}) {
    const lang =
      typeof HM_Variables !== "undefined" && HM_Variables.language === "en"
        ? "en"
        : "vi";
    const code = res?.error_code || "";

    if (code === "rate_limit_phone") {
      const limit = Number(
        res.daily_limit_phone ||
          HM_Variables?.register_otp_daily_send_limit ||
          5,
      );
      const template =
        HM_Variables?.frontend_lang?.otp_send_rate_limit_phone ||
        (lang === "en"
          ? "You have reached the maximum of %d OTP sends today for this phone number. Please try again tomorrow."
          : "Bạn đã gửi OTP tối đa %d lần trong ngày với số điện thoại này. Vui lòng thử lại vào ngày mai.");
      return String(template).replace(/%d/g, String(Math.max(1, limit)));
    }

    if (code === "rate_limit_ip") {
      const limit = Number(
        res.daily_limit_ip ||
          HM_Variables?.register_otp_daily_send_limit_ip ||
          5,
      );
      const template =
        HM_Variables?.frontend_lang?.otp_send_rate_limit_ip ||
        (lang === "en"
          ? "You have reached the maximum of %d OTP sends today. Please try again tomorrow."
          : "Bạn đã gửi OTP tối đa %d lần trong ngày. Vui lòng thử lại vào ngày mai.");
      return String(template).replace(/%d/g, String(Math.max(1, limit)));
    }

    if (code === "resend_cooldown") {
      return (
        HM_Variables?.frontend_lang?.otp_send_resend_cooldown ||
        (lang === "en"
          ? "Please wait before requesting another OTP code."
          : "Vui lòng đợi trước khi gửi lại mã OTP.")
      );
    }

    if (code === "otp_send_blocked" || res?.call_lock) {
      return (
        HM_Variables?.frontend_lang?.otp_verify_call_lock ||
        (lang === "en"
          ? "OTP limit reached. Please try again tomorrow."
          : "Đã đạt giới hạn OTP trong ngày. Vui lòng thử lại vào ngày mai.")
      );
    }

    return (
      t("booking_danhy_register_failed") ||
      (lang === "en"
        ? "Registration failed. Please check your details and try again."
        : "Đăng ký thất bại. Vui lòng kiểm tra thông tin và thử lại.")
    );
  }

  openRegisterOtpFlow({ identifier, password, retryAfter = 0 } = {}) {
    if (!window.HM_OTP_FLOW) {
      return;
    }

    this.closeRegisterModal();
    window.HM_OTP_FLOW.open({
      mode: "register",
      identifier,
      password,
      skipInitialSend: true,
      retryAfter,
      onVerified: async () => {
        this.openRegisterSuccess();
      },
    });
  }

  openRegisterSuccess() {
    if (!this.$modal.length) {
      return;
    }

    this.closeRegisterModal();
    this._pendingRegisterLogin = true;
    this.reset({ restoreLayout: false });
    $("body").addClass("show-modal");
    this.$modal.addClass("show-modal").attr("aria-hidden", "false");
    this.$modal.find(".modal-dialog").addClass("active");
    this.showResult("register_success");
  }

  async onRegisterSubmit() {
    if (!this.$registerForm?.length || this._registerSubmitInFlight) {
      return;
    }

    this.clearRegisterFormErrors();

    const identifier = String(
      this.$registerForm.find('input[name="danhy_register_identifier"]').val() ||
        "",
    ).trim();
    const password = String(
      this.$registerForm.find('input[name="danhy_register_password"]').val() ||
        "",
    );
    const confirm = String(
      this.$registerForm
        .find('input[name="danhy_register_password_confirm"]')
        .val() || "",
    );

    this.updatePasswordPolicies(password);

    const lang =
      typeof HM_Variables !== "undefined" && HM_Variables.language === "en"
        ? "en"
        : "vi";
    const requiredMsg =
      lang === "en" ? "This field is required." : "Vui lòng điền thông tin này.";
    const mismatchMsg =
      lang === "en"
        ? "Passwords do not match."
        : "Mật khẩu xác nhận không khớp.";
    const policyMsg =
      HM_Variables?.frontend_lang?.booking_danhy_register_password_policy ||
      (lang === "en"
        ? "Password does not meet the requirements."
        : "Mật khẩu chưa đáp ứng yêu cầu.");

    let valid = true;

    const markError = (name, message) => {
      const $input = this.$registerForm.find(`input[name="${name}"]`);
      $input.addClass("err");
      $input.closest(".input-group").find(".err-msg").text(message);
      valid = false;
    };

    if (!identifier) {
      markError("danhy_register_identifier", requiredMsg);
    } else if (!this.isValidRegisterIdentifier(identifier)) {
      markError(
        "danhy_register_identifier",
        t("booking_danhy_register_identifier_invalid") ||
          "Email/Phone is not in the correct format.",
      );
    }

    if (!password) {
      markError("danhy_register_password", requiredMsg);
    } else if (!this.isPasswordPolicyMet(password)) {
      markError("danhy_register_password", policyMsg);
    }

    // Confirm field: only required + match password (no policy rules).
    if (!confirm) {
      markError("danhy_register_password_confirm", requiredMsg);
    } else if (password !== confirm) {
      markError("danhy_register_password_confirm", mismatchMsg);
    }

    if (!valid) {
      return;
    }

    this._registerSubmitInFlight = true;
    this.setRegisterSubmitLoading(true);

    try {
      const res = await danhyApi("register_send_otp", undefined, {
        username: identifier,
        password,
      });

      if (!res?.success) {
        markError(
          "danhy_register_identifier",
          this.getRegisterSendErrorMessage(res),
        );
        this.syncRegisterSubmitState();
        return;
      }

      this.openRegisterOtpFlow({
        identifier,
        password,
        retryAfter: Number(res.retry_after || 0),
      });
    } catch (error) {
      console.error("register_send_otp failed", error);
      markError(
        "danhy_register_identifier",
        lang === "en"
          ? "Connection error. Please try again."
          : "Lỗi kết nối. Vui lòng thử lại.",
      );
      this.syncRegisterSubmitState();
    } finally {
      this._registerSubmitInFlight = false;
      this.setRegisterSubmitLoading(false);
      this.syncRegisterSubmitState();
    }
  }

  openModal() {
    if (!this.$modal.length) {
      return;
    }

    this.closeRegisterModal();
    this._pendingRegisterLogin = false;

    // Reset layout before showing so a previous success/error state never flashes.
    this.reset();
    $("body").addClass("show-modal");
    this.$modal.addClass("show-modal").attr("aria-hidden", "false");
    this.$modal.find(".modal-dialog").addClass("active");
    this.connectSocket();
    this.initQr();
  }

  setCtaLoading(isLoading) {
    $(".booking-danhy-cta")
      .prop("disabled", !!isLoading)
      .toggleClass("disabled", !!isLoading);
  }

  hasAbandonedOauthReturn() {
    try {
      const params = new URLSearchParams(window.location.search || "");
      return ["code", "state", "session_state", "iss", "error", "error_description"].some(
        (key) => params.has(key),
      );
    } catch (error) {
      return false;
    }
  }

  stripAbandonedOauthParams() {
    try {
      const url = new URL(window.location.href);
      ["code", "state", "session_state", "iss", "error", "error_description"].forEach(
        (key) => url.searchParams.delete(key),
      );
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, document.title, next);
    } catch (error) {
      // Ignore URL cleanup failures (older browsers / odd environments).
    }
  }

  startHoanMyLogin() {
    if (isDanhyMobileDevice()) {
      this.openDanhyAppDeeplink();
      return;
    }
    this.openModal();
  }

  /**
   * Leftover Keycloak returns (?code=&state=) no longer exchange tokens.
   * Strip OAuth params and send the user through Hoan My login instead.
   */
  consumeAbandonedKeycloakReturn() {
    if (!this.hasAbandonedOauthReturn()) {
      return false;
    }

    this.stripAbandonedOauthParams();
    return true;
  }

  openDanhyAppDeeplink() {
    launchDanhyAppOrStore({
      onBusyChange: (busy) => this.setCtaLoading(busy),
    });
  }

  connectSocket() {
    if (this._stomp?.connected) {
      return Promise.resolve();
    }

    if (this._socketReady) {
      return this._socketReady;
    }

    // Client still reconnecting — wait for the next onConnect.
    if (this._stomp?.active) {
      this._socketReady = new Promise((resolve, reject) => {
        this._socketReadyResolve = resolve;
        this._socketReadyReject = reject;
      });
      return this._socketReady;
    }

    const rest = (HM_Variables?.rest_url || "").replace(/\/$/, "");
    if (!rest) {
      console.error("Missing REST URL");
      return Promise.reject(new Error("Missing REST URL"));
    }

    this._socketReady = new Promise((resolve, reject) => {
      this._socketReadyResolve = resolve;
      this._socketReadyReject = reject;

      this._stomp = new Client({
        webSocketFactory: () =>
          new SockJS(`${rest}/hmc/v1/danhy-ws/qr`, null, {
            // Polling usually connects faster through the PHP SockJS proxy than
            // xhr-streaming (which waits on a long-lived upstream stream).
            transports: ["xhr-polling", "xhr-streaming"],
            timeout: 4000,
          }),
        reconnectDelay: 3000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: () => {
          if (this.sessionId) {
            this.subscribeSession(this.sessionId);
          }
          const resolveReady = this._socketReadyResolve;
          this._socketReadyResolve = null;
          this._socketReadyReject = null;
          resolveReady?.();
        },
        onWebSocketClose: () => {
          // Keep a pending first-connect waiter; otherwise clear so the next
          // ensureSocketConnected() waits for reconnect.
          if (this._socketReadyResolve) {
            return;
          }
          this._socketReady = null;
        },
        onStompError: (frame) => {
          console.error("STOMP error", frame.headers?.message, frame.body);
        },
      });

      this._stomp.activate();
    });

    return this._socketReady;
  }

  async ensureSocketConnected(timeoutMs = 15000) {
    const ready = this.connectSocket();
    let timer = null;

    try {
      await Promise.race([
        ready,
        new Promise((_, reject) => {
          timer = setTimeout(
            () => reject(new Error("Socket connect timeout")),
            timeoutMs,
          );
        }),
      ]);
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
    }

    if (!this._stomp?.connected) {
      throw new Error("Socket not connected");
    }
  }

  subscribeSession(sessionId) {
    this.unsubscribeSession();
    if (!this._stomp?.connected || !sessionId) {
      return false;
    }

    this._subscription = this._stomp.subscribe(
      `/topic/qr/${sessionId}`,
      (message) => {
        const payload = JSON.parse(message.body || "{}");
        this.onQrEvent(payload);
      },
    );
    return true;
  }

  unsubscribeSession() {
    this._subscription?.unsubscribe();
    this._subscription = null;
  }

  disconnectSocket() {
    this.unsubscribeSession();

    const rejectReady = this._socketReadyReject;
    this._socketReady = null;
    this._socketReadyResolve = null;
    this._socketReadyReject = null;

    if (this._stomp?.active) {
      this._stomp.deactivate();
    }
    this._stomp = null;

    if (rejectReady) {
      rejectReady(new Error("Socket disconnected"));
    }
  }

  onQrEvent(socketPayload) {
    const type = String(socketPayload?.type || "").toUpperCase();
    const payload = socketPayload?.payload;

    if (type === "SCANNED") {
      this.showScanningOverlay();
      return;
    }

    if (type === "APPROVED") {
      this.exchangeQr(payload);
      return;
    }

    if (type === "REJECTED") {
      this.showResult("rejected");
      return;
    }

    if (type === "ERROR" || type === "FAILED") {
      this.showResult("error");
    }
  }

  exchangeQr(exchangeCode) {
    if (this._exchanging) {
      return;
    }

    this._exchanging = true;
    this.stopTimer();
    this.showScanningOverlay();
    this.unsubscribeSession();

    const code = String(exchangeCode || "");
    const sessionId = this.sessionId;
    if (!code || !sessionId) {
      this._exchanging = false;
      this.showResult("error");
      return;
    }

    if (!this._abortController || this._abortController.signal.aborted) {
      this._abortController = new AbortController();
    }

    danhyApi("qr_exchange", this._abortController.signal, {
      exchange_code: code,
      session_id: sessionId,
    })
      .then((res) => {
        if (!res?.success) {
          this.showResult("error");
          return;
        }

        if (res.stored === "memory") {
          const data = res.data || {};
          this.storeMemoryAccessToken(
            data.accessToken || data.access_token || null,
            data.expiresIn || data.expires_in,
          );
        } else {
          this.clearAccessToken();
        }

        this.hideDanhyLogin();
        this.showResult("success");
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          return;
        }
        console.error("qr_exchange failed");
        this.showResult("error");
      })
      .finally(() => {
        this._exchanging = false;
      });
  }

  spinnerHtml(labelKey) {
    const assetsUrl = HM_Variables?.assets_url || "";
    return `<span class="booking-danhy-qr-spinner" role="status" aria-label="${t(labelKey)}">
        <img src="${assetsUrl}img/icons/loader-ico.svg" alt="" width="24" height="24" />
      </span>`;
  }

  stopTimer() {
    clearInterval(this._expiryTimer);
    this._expiryTimer = null;
  }

  showScanningOverlay() {
    // Countdown stays visible while waiting for approve/reject.
    if (this.$qrExpiry.hasClass("expired")) {
      return;
    }
    if (!this.$qrMain.find("img[alt='QR']").length) {
      return;
    }

    this.$qrMain.find(".booking-danhy-qr-overlay").remove();
    this.$qrMain.append(
      `<div class="booking-danhy-qr-overlay booking-danhy-qr-overlay--scanning">${this.spinnerHtml("booking_danhy_qr_scanning")}</div>`,
    );
  }

  /**
   * Single source of truth for which body section is visible.
   * @param {"qr"|"result"} view
   */
  setView(view) {
    if (!this.$modal.length) {
      return;
    }
    this.$modal.attr("data-view", view);
  }

  restoreDefaultLayout() {
    this.$modal.removeClass("is-success is-error");
    this.setView("qr");
    this.$title.html(this.defaultTitle).removeClass("red-txt").addClass("blue-txt");
    this.$desc.html(this.defaultDesc);
    this.$resultIcon.attr("src", "");
    this.$submit.prop("hidden", false);
    this.$continue.prop("hidden", true);
    this.$retry.prop("hidden", true);
  }

  showResult(kind) {
    // Ignore late async failures after the QR modal was closed (e.g. Register).
    if (!this.isQrModalOpen()) {
      return;
    }

    this.stopTimer();
    this.unsubscribeSession();
    // Drop the warm socket only after a completed login; keep it for retry/register→QR.
    if (kind === "success") {
      this.disconnectSocket();
    }
    const assetsUrl = HM_Variables?.assets_url || "";
    const isSuccess = kind === "success" || kind === "register_success";
    const copy =
      kind === "register_success"
        ? {
            title: "booking_danhy_register_success_title",
            desc: "booking_danhy_register_success_desc",
          }
        : kind === "success"
          ? { title: "booking_danhy_success_title", desc: "booking_danhy_success_desc" }
          : kind === "rejected"
            ? { title: "booking_danhy_rejected_title", desc: "booking_danhy_rejected_desc" }
            : kind === "profile_error"
              ? { title: "booking_danhy_profile_error_title", desc: "booking_danhy_profile_error_desc" }
              : { title: "booking_danhy_error_title", desc: "booking_danhy_error_desc" };

    this.setView("result");
    // is-success/is-error still drive the footer button (continue/retry) visibility.
    this.$modal
      .removeClass("is-success is-error")
      .addClass(isSuccess ? "is-success" : "is-error");
    this.$title
      .text(t(copy.title))
      .toggleClass("blue-txt", isSuccess)
      .toggleClass("red-txt", !isSuccess);
    this.$desc.text(t(copy.desc));
    this.$resultIcon.attr(
      "src",
      `${assetsUrl}img/icons/${isSuccess ? "check-circle-ico" : "x-circle-ico"}.svg`,
    );
    this.$submit.prop("hidden", true);
    this.$continue.prop("hidden", !isSuccess);
    this.$retry.prop("hidden", isSuccess);
  }

  reset({ restoreLayout = true } = {}) {
    this.unsubscribeSession();
    this._abortController?.abort();
    this._abortController = null;
    this.stopTimer();
    this.sessionId = null;
    this._exchanging = false;
    if (!restoreLayout) {
      return;
    }
    this.restoreDefaultLayout();
    this.$qrMain.empty();
    this.$qrExpiry.empty().removeClass("expired");
  }

  async initQr() {
    if (!this.$modal.length) {
      return;
    }

    // Warm/reuse SockJS; QR init runs in parallel with connect.
    this.connectSocket();
    this.reset();
    this.$qrMain.html(this.spinnerHtml("booking_danhy_qr_loading"));
    this._abortController = new AbortController();

    try {
      // max(socket, qr_init) instead of socket-then-init — still subscribe before paint.
      const [, res] = await Promise.all([
        this.ensureSocketConnected(),
        danhyApi("qr_init", this._abortController.signal),
      ]);

      if (this._abortController.signal.aborted || !this.isQrModalOpen()) {
        return;
      }
      if (!res?.success) {
        this.showResult("error");
        return;
      }
      this.renderQr(res.data);
    } catch (error) {
      if (
        error?.name === "AbortError" ||
        !this.isQrModalOpen()
      ) {
        return;
      }
      console.error("qr_init failed", error);
      this.showResult("error");
    }
  }

  renderQr(data) {
    const source = data?.data && typeof data.data === "object" ? data.data : data || {};
    const content = source.qr_content;
    const assetsUrl = HM_Variables?.assets_url || "";
    const resetOverlay = `
      <div class="booking-danhy-qr-overlay">
        <button type="button" class="btn btn-danhy-qr-refresh">
          <img src="${assetsUrl}img/icons/refresh-ico.svg" alt="refresh" />
        </button>
      </div>`;
    this.sessionId = source.session_id || null;

    // Never paint the QR until the topic subscription is attached.
    if (!this.subscribeSession(this.sessionId)) {
      this.showResult("error");
      return;
    }

    this.$qrMain.empty();
    if (!content) {
      this.showResult("error");
      return;
    }

    this.$qrMain.append($("<img>", { src: encodeQr(content), alt: "QR" }));

    const rawExpiry = Number(source.expired_at);
    const expiredAt = rawExpiry > 1e12 ? rawExpiry : rawExpiry > 1e9 ? rawExpiry * 1000 : 0;
    if (!expiredAt) {
      this.$qrExpiry.empty().removeClass("expired");
      return;
    }

    const tick = () => {
      if (this._exchanging) {
        return;
      }

      const remaining = Math.ceil((expiredAt - Date.now()) / 1000);
      if (remaining <= 0) {
        this.stopTimer();
        this.unsubscribeSession();
        this.$qrExpiry.html(t("booking_danhy_qr_expired")).addClass("expired");
        // Drop scanning overlay (if any) and show refresh.
        this.$qrMain.find(".booking-danhy-qr-overlay").remove();
        this.$qrMain.append(resetOverlay);
        this.$qrMain.find(".btn-danhy-qr-refresh").on("click", (event) => {
          event.preventDefault();
          this.initQr();
        });
        return;
      }

      this.$qrExpiry
        .html(t("booking_danhy_qr_expiry", { time: remaining }))
        .removeClass("expired");
    };

    tick();
    this._expiryTimer = setInterval(tick, 1000);
  }
}

export { unlockDanhyLockedFields };
